import { parseSpecification, combineSpecs } from "./formatters";

/**
 * UI에서 사용하는 Item 형식
 * 폼 입력 및 React 렌더링에 최적화된 구조
 */
export interface UiItem {
  id?: string;
  name: string;
  specification?: string; // 단일 입력 필드
  quantity: number;
  unit: string;
  itemId?: string; // 자동완성 선택 시 부여되는 마스터 품목 ID
  isAutocompleteSelected?: boolean; // 자동완성 여부
  amount?: number; // 예상 금액
  unitPrice?: number; // 단가
  [key: string]: unknown; // 기타 UI 전용 상태 (예: canAutoQuote 등)
}

/**
 * UI Item을 API Item(CreateQuoteRequestDto의 items 요소)으로 변환
 *
 * 변환 규칙:
 * 1. UI 전용 필드 (id, isAutocompleteSelected 등) 제외
 * 2. specification -> spec1 (입력 한 줄을 그대로 보존 — parseSpecification 참고)
 * 3. 필수값(amount) 누락 시 기본값 0 설정
 * 4. quantity를 안전하게 숫자로 변환
 *
 * @param uiItem UI 폼에서 사용 중인 Item
 * @returns API에 전송할 Item (CreateQuoteRequestDto 구조)
 */
export function transformUiItemToApiItem(
  uiItem: UiItem,
): CreateQuoteRequestItemDto {
  const specs = parseSpecification(uiItem.specification);

  const apiItem: CreateQuoteRequestItemDto = {
    name: uiItem.name,
    quantity: Number(uiItem.quantity) || 0,
    // 화면 검증은 앞뒤 공백을 뗀 단위로 하므로 보낼 때도 뗀다 — « 박스 » 가 2단계를 통과하고
    // 제출 때 서버 형식 검사에 걸리던 것(GS A2-17).
    unit: typeof uiItem.unit === "string" ? uiItem.unit.trim() : uiItem.unit,
    // 사양 정보 분리
    spec1: specs.spec1,
    spec2: specs.spec2,
    spec3: specs.spec3,
    // API 필수값 방어 코드 (프론트엔드 견적 요청의 경우 사용자는 금액을 모름)
    amount: uiItem.amount || 0,
  };

  // 자동완성 데이터가 있으면 보존
  if (uiItem.itemId) {
    apiItem.itemId = uiItem.itemId;
  }

  return apiItem;
}

/**
 * 여러 UI Item을 API Item 배열로 변환
 */
export function transformUiItemsToApiItems(
  uiItems?: UiItem[] | null,
): CreateQuoteRequestItemDto[] {
  if (!uiItems || !Array.isArray(uiItems) || uiItems.length === 0) return [];
  return uiItems.map(transformUiItemToApiItem);
}
import type {
  QuoteItemDto,
  QuoteResponseItemDto,
  QuoteMessageResponseDtoQuoteDataItemsItem,
  CreateQuoteRequestItemDto,
} from "@semo/api-contracts";

/**
 * API에서 전달받는 아이템 DTO들의 통합 타입
 * 프론트엔드와 백엔드의 다양한 DTO 포맷을 유연하게 수용하기 위해 Partial을 사용합니다.
 */
export type ApiItem = Partial<
  QuoteItemDto &
    QuoteResponseItemDto &
    QuoteMessageResponseDtoQuoteDataItemsItem &
    CreateQuoteRequestItemDto
>;

/**
 * API에서 받은 Item(DB 형식)을 UI Item으로 변환
 *
 * 변환 규칙:
 * 1. spec1, spec2, spec3 -> specification 단일 필드로 통합
 * 2. React Key 용도의 고유 id 생성 (없을 경우)
 *
 * @param apiItem API 응답 Item (DTO)
 * @returns UI에 렌더링하기 좋은 Item 구조
 */
export function transformApiItemToUiItem(apiItem: ApiItem): UiItem {
  const combinedSpec = combineSpecs(apiItem);

  // QuoteResponseItemDto에는 id가 있고 QuoteItemDto에는 없을 수 있음
  const id =
    "id" in apiItem && apiItem.id ? String(apiItem.id) : crypto.randomUUID();

  return {
    ...apiItem,
    id,
    name: apiItem.name || "",
    quantity: Number(apiItem.quantity) || 1,
    unit: apiItem.unit || "",
    specification: combinedSpec === "-" ? undefined : combinedSpec, // '-' 대신 빈 값으로 처리
    isAutocompleteSelected: !!apiItem.itemId, // itemId가 있으면 자동완성 선택으로 간주
  };
}

/**
 * 여러 API Item을 UI Item 배열로 변환
 */
export function transformApiItemsToUiItems(
  apiItems?: ApiItem[] | null,
): UiItem[] {
  if (!apiItems || !Array.isArray(apiItems) || apiItems.length === 0) return [];
  return apiItems.map(transformApiItemToUiItem);
}
